<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import { BrazeCard } from '~/types/Content'

type BrazeContentCardFeedProps = {
  feedType?: string
  variant?: 'unknown'
  supportingData?: {
    home?: {}
  }
}
const props = defineProps<BrazeContentCardFeedProps>()
const { $braze } = useNuxtApp()
const contentCards = ref<BrazeCard[] | undefined>(undefined)
const cardSection = ref(null)
let hasLoggedImpressions = false

useIntersectionObserver(cardSection, ([{ isIntersecting }]) => {
  if (isIntersecting && contentCards.value && !hasLoggedImpressions) {
    hasLoggedImpressions = $braze.sdk.logContentCardImpressions(contentCards.value)
    $braze.debugLogger(`Logged content card impressions: ${hasLoggedImpressions}`)
  }
})

function setContentCards() {
  /* NOTE: need to filter out control cards by default
   * braze sdk does not do this when using a custom ui and control cards
   * hold no information to show on a card but they still take up a value in the array
   */
  if (!props.feedType) {
    contentCards.value = $braze.sdk.getCachedContentCards()?.cards?.filter((card) => !card.isControl)
    return
  }
  contentCards.value = $braze.sdk
    .getCachedContentCards()
    ?.cards?.filter((card) => card.extras['feed_type'] === props.feedType && !card.isControl)
}

onMounted(() => {
  setContentCards()
})
</script>

<template>
  <section v-if="contentCards && contentCards.length > 0" class="content-card-panel" ref="cardSection">
    <BrazeContentCard v-for="card in contentCards" :key="card.id" :card="card" :variant="variant" />
  </section>
</template>

<style lang="postcss">
.content-card-panel {
  background: var(--surface-light-20);
  margin-bottom: var(--space-100);
  padding: var(--space-100) var(--space-60);
  display: flex;
  flex-direction: column;
  gap: var(--space-60);
  justify-content: center;
  align-items: center;

  @media (--tablet) {
    padding: var(--space-100) 8rem;
  }
}
</style>
